import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore, shallowEqual } from 'react-redux';
import Close from '@mui/icons-material/Close';
import PersonPinCircleSharpIcon from '@mui/icons-material/PersonPinCircleSharp';
import {
  TextField,
  List,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';

import notifyBugsnag from '../../../util/notifyBugsnag';
import { googleMapsErrorState } from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import useClasses from '../../../hooks/legacy/useClasses';
import { useWhere } from '../../../hooks/useWhere';
import useGooglePlaces from '../../../hooks/useGooglePlaces';
import checkoutContext from '../../../util/checkoutContext';

import styles from './GoogleAutoCompleteInput.style';

const debounceSearchMinLength = 2;
const autocompleteMaxResults = 10;

export default function GoogleAutoCompleteInput({
  showIcon,
  streetAddress,
  setStreetAddress,
  disabled,
  onAfterSelect,
  helperText,
  initialValue,
}) {
  const dispatch = useDispatch();
  const store = useStore();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const classes = useClasses(styles);
  const { setSelectedAddress } = useWhere();
  const [onSearch, setOnSearch] = useState(true);
  const selecting = useRef(false);
  const inputGeneration = useRef(0);

  const {
    placePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading,
    fetchPlaceDetails,
    placePredictionsError,
  } = useGooglePlaces({
    regionCodes: ['us'],
  });

  const [addressString, setAddressString] = useState(initialValue || '');

  const { address, googleMapsErrorStates } = useSelector(
    ({ address: selectedAddress, main, profile }) => ({
      address: selectedAddress.selectedAddress || null,
      googleMapsErrorStates: main.googleMapsErrorState,
      addresses: profile.getAddressesResponse?.response,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (initialValue != null) return;
    if (address) {
      setAddressString(
        showIcon ? address?.address || '' : address?.entered_address || ''
      );
      dispatch(googleMapsErrorState(false));
    }
  }, [address, showIcon, initialValue]);

  const onInputChange = (evt) => {
    inputGeneration.current += 1;
    if (!onSearch) {
      setOnSearch(true);
    }

    if (!evt.target.value && Object.keys(streetAddress).length > 0) {
      setStreetAddress({ ...streetAddress, route: '' });
    }
    setAddressString(evt.target.value);
    if (!showIcon && initialValue == null) {
      // Typed text is not the structured Google selection. Preserve the text
      // but do not let Update submit the previous destination behind it.
      setSelectedAddress({ entered_address: evt.target.value });
    }

    getPlacePredictions({
      input:
        evt.target.value.length > debounceSearchMinLength
          ? evt.target.value
          : '',
    });
  };

  const onPlaceSelected = async (place) => {
    if (disabled || selecting.current) return;
    selecting.current = true;
    setOnSearch(false);
    const context = checkoutContext(store.getState());
    const selected = store.getState().address.selectedAddress;
    const { current: generation } = inputGeneration;

    try {
      const details = await fetchPlaceDetails(place.place_id);
      if (
        !mounted.current ||
        inputGeneration.current !== generation ||
        checkoutContext(store.getState()) !== context ||
        store.getState().address.selectedAddress !== selected
      )
        return;
      if (!details) return;

      const { formattedAddress, addressComponents, location } = details;

      if (!location) return;

      const getAddressItem = (key) =>
        addressComponents?.find((c) => c.types.includes(key))?.shortText;

      const route = getAddressItem('route');
      const number = getAddressItem('street_number');

      // Prefer sublocality (village/neighbourhood) over the legal municipality
      // (locality) so addresses like "Ivoryton" are not silently changed to
      // "Essex" by Google's normalisation.
      const city =
        getAddressItem('sublocality_level_1') ||
        getAddressItem('sublocality') ||
        getAddressItem('neighborhood') ||
        getAddressItem('locality') ||
        getAddressItem('administrative_area_level_2');

      const state = getAddressItem('administrative_area_level_1');
      const zip = getAddressItem('postal_code');

      const lat =
        typeof location.lat === 'function' ? location.lat() : location.lat;
      const lng =
        typeof location.lng === 'function' ? location.lng() : location.lng;

      if (
        lat == null ||
        lng == null ||
        !Number.isFinite(lat) ||
        !Number.isFinite(lng)
      )
        return;

      const resolvedStreetAddress = `${number ?? ''} ${route ?? ''}`.trim();

      // Rebuild display address from resolved components so entered_address
      // matches the saved city field. Format: "<street>, <city>, <state> <zip>"
      const stateZip = [state, zip].filter(Boolean).join(' ');
      const resolvedFullAddress = [
        resolvedStreetAddress || route || '',
        city,
        stateZip,
      ]
        .filter(Boolean)
        .join(', ');
      const displayAddress = resolvedFullAddress || formattedAddress;

      const selectedAddress = {
        entered_address: displayAddress,
        address: resolvedStreetAddress || formattedAddress,
        city,
        state,
        zipcode: zip,
        latitude: lat,
        longitude: lng,
        placeId: place.place_id,
      };
      setSelectedAddress(selectedAddress);

      setAddressString(
        showIcon ? resolvedStreetAddress || formattedAddress : displayAddress
      );

      if (setStreetAddress) {
        setStreetAddress({ route, number, city, state, zip });
      }

      if (onAfterSelect) {
        onAfterSelect();
      }
      getPlacePredictions('');
    } catch (err) {
      if (mounted.current) setOnSearch(true);
      notifyBugsnag(err);
    } finally {
      selecting.current = false;
    }
  };

  const clearAddress = () => {
    inputGeneration.current += 1;
    setSelectedAddress(null);
    setAddressString('');
    setOnSearch(true);
    getPlacePredictions('');
  };

  return (
    <div>
      <Box className={classes.inputContainer}>
        <span className={classes.addressPin} data-test-id="address-pin">
          {!showIcon && (
            <PersonPinCircleSharpIcon
              color={googleMapsErrorStates ? 'error' : 'primary'}
            />
          )}
        </span>
        <TextField
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          placeholder="Enter delivery address"
          style={{
            borderColor: googleMapsErrorStates ? '#EE1411' : '#D1D1D1',
            width: '100%',
            paddingLeft: '32px',
            height: '50px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
          sx={{
            '& .MuiInputBase-input': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          className={showIcon ? classes.newAddressInput : classes.addressInput}
          onChange={onInputChange}
          loading={isPlacePredictionsLoading}
          value={addressString}
          data-test-id="autocomplete-search"
          type="text"
          disabled={disabled}
        />
        {addressString && !showIcon && (
          <IconButton
            size="small"
            onClick={clearAddress}
            data-test-id="clear-address"
            aria-label="Clear address"
            disabled={disabled}
            sx={{
              position: 'relative',
              marginLeft: '-35px',
              marginTop: '5px',
              padding: '5px',
              color: 'text.secondary',
            }}
          >
            <Close fontSize="small" aria-hidden="true" />
          </IconButton>
        )}
      </Box>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, ml: 1 }}
        >
          {helperText}
        </Typography>
      )}
      {placePredictionsError && onSearch && !disabled && (
        <Box role="alert">
          <Typography variant="caption">
            Address search is unavailable. Your entry has been kept.
          </Typography>
          <Button
            data-test-id="retry-address-search"
            onClick={() => getPlacePredictions(addressString)}
          >
            Retry address search
          </Button>
        </Box>
      )}
      {addressString &&
        addressString.length > debounceSearchMinLength &&
        onSearch &&
        placePredictions !== null &&
        placePredictions.length > 0 &&
        !disabled && (
          <Box>
            <List
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                overflow: 'auto',
                maxHeight: 120,
              }}
            >
              {placePredictions.map((item, index) => {
                if (index > autocompleteMaxResults) {
                  return null;
                }
                return (
                  <Box sx={{ borderRadius: '16px' }} key={item?.place_id}>
                    <ListItemButton
                      data-test-id={`google-autocomplete-item-${item?.place_id}`}
                    >
                      <ListItemText
                        primary={item?.description}
                        primaryTypographyProps={{ fontSize: '14px' }}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'left',
                          alignItems: 'left',
                          textAlign: 'left',
                        }}
                        data-test-id={`google-autocomplete-item-action-${item?.place_id}`}
                        onClick={() => onPlaceSelected(item)}
                      />
                    </ListItemButton>
                  </Box>
                );
              })}
            </List>
          </Box>
        )}
    </div>
  );
}

GoogleAutoCompleteInput.propTypes = {
  setStreetAddress: PropTypes.func,
  showIcon: PropTypes.bool,
  streetAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  onAfterSelect: PropTypes.func,
  helperText: PropTypes.string,
  initialValue: PropTypes.string,
};

GoogleAutoCompleteInput.defaultProps = {
  setStreetAddress: () => null,
  showIcon: false,
  streetAddress: '',
  disabled: false,
  onAfterSelect: null,
  helperText: null,
  initialValue: null,
};
